import React, { RefObject } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form';

import ColorSlider from '@/components/atoms/ColorSlider';
import DatePickerField from '@/components/atoms/DatePickerField';
import IconSelect from '@/components/atoms/IconSelect';
import InputField from '@/components/atoms/InputField';
import SelectField from '@/components/atoms/SelectField';
import SliderField from '@/components/atoms/SliderField';
import SwitchField from '@/components/atoms/SwitchField';
import TextAreaField from '@/components/atoms/TextAreaField';

import { BUDGET_ICONS } from '@/utils/budgetIcons';
import { BudgetFormValues } from '@/components/organisms/budget/form-steps/budgetStep.schema';

import euroIcon from '@/assets/icons/euro.svg';

const periods = [
  { value: 'one_month', label: '1 Month' },
  { value: 'six_month', label: '6 Month' },
  { value: 'one_year', label: '1 Year' }
];

const currencies = [
  { value: 'USD', label: 'US Dollar' },
  { value: 'EUR', label: 'Euro' },
  { value: 'GBP', label: 'British Pound' }
];

interface BudgetStepProps {
  register: UseFormRegister<BudgetFormValues>;
  watch: UseFormWatch<BudgetFormValues>;
  setValue: UseFormSetValue<BudgetFormValues>;
  trigger: UseFormTrigger<BudgetFormValues>;
  errors: FieldErrors<BudgetFormValues>;
  iconSelectRef: RefObject<HTMLDivElement>;
}

const BudgetStep: React.FC<BudgetStepProps> = ({ register, watch, setValue, trigger, errors, iconSelectRef }) => {
  const selectedIcon = watch('budgetIcon');
  const selectedColor = watch('budgetColor');

  return (
    <>
      <div className="space-y-6 w-full flex flex-col items-start justify-between lg:flex-row lg:gap-5 lg:space-y-0">
        <InputField
          className="flex-1 w-full"
          label="Name of budget"
          placeholder="Enter budget name..."
          name="budgetName"
          type="text"
          register={register}
          error={errors.budgetName}
        />
        <InputField
          className="flex-1 w-full"
          label="Budget type"
          placeholder="Enter budget type..."
          name="budgetType"
          type="text"
          register={register}
          error={errors.budgetType}
        />
      </div>
      <div className="space-y-6 w-full flex flex-col items-start justify-between lg:flex-row lg:gap-5 lg:space-y-0">
        <div className="flex-1">
          <IconSelect
            ref={iconSelectRef}
            name="budgetIcon"
            value={selectedIcon}
            onChange={(value) => {
              setValue('budgetIcon', value);
              trigger('budgetIcon');
            }}
            icons={BUDGET_ICONS}
            strokeColor={selectedColor || '#495057'}
            error={errors.budgetIcon?.message || ''}
          />
          {errors.budgetIcon && <p className="mt-2 text-xs text-red-600">Budget icon is required</p>}
          <ColorSlider
            className="mt-4"
            value={selectedColor}
            onChange={(color) => setValue('budgetColor', color)}
            title="Select a colour for your budget icon"
          />
        </div>
        <TextAreaField
          name="description"
          label="Budget Description"
          placeholder="Enter description..."
          register={register}
          className="flex-1 w-full"
          error={errors.description}
        />
      </div>
      <div className="flex md:flex-col items-end justify-between gap-3 lg:flex-row ">
        <div className="flex-1 flex items-start gap-3">
          <DatePickerField
            name="startDate"
            label="Budget starting date"
            register={register}
            value={watch('startDate')}
            onChange={(date) => date && setValue('startDate', date)}
            className="flex-1"
            error={errors.startDate}
          />
          <DatePickerField
            name="endDate"
            label="Budget ending date"
            register={register}
            value={watch('endDate')}
            onChange={(date) => date && setValue('endDate', date)}
            className="flex-1"
            error={errors.endDate}
          />
        </div>
        <SelectField
          name="period"
          options={periods}
          value={watch('period')}
          onChange={(value) => {
            setValue('period', value as string);
          }}
          variant="icon-only"
          className="md:flex-1 md:hidden"
        />
        <SelectField
          name="period"
          label="Select Period"
          placeholder="Select Period..."
          options={periods}
          register={register}
          value={watch('period')}
          onChange={(value) => {
            setValue('period', value as string);
          }}
          className="hidden md:block md:w-full flex-1"
        />
      </div>
      <div className="space-y-6 w-full flex flex-col md:items-start justify-between md:flex-row md:gap-5 md:space-y-0">
        <SelectField
          name="currency"
          label="Currency"
          placeholder="Select Currency"
          options={currencies}
          register={register}
          value={watch('currency')}
          onChange={(value) => {
            setValue('currency', value as string);
          }}
          className="flex-1"
          error={errors.currency}
        />
        <SwitchField
          name="autoRenewal"
          label="Automatic Renewal"
          register={register}
          checked={watch('autoRenewal')}
          onCheckedChange={(checked) => setValue('autoRenewal', checked)}
          className="flex-1"
        />
      </div>

      <div className="space-y-6 w-full flex flex-col justify-between md:flex-row md:gap-5 md:space-y-0">
        <div className="flex flex-col justify-between w-full flex-1 space-y-6">
          <InputField
            name="bmev"
            label="Budget maximum expense value (BMEV)"
            placeholder="e.g 100000"
            type="number"
            icon={euroIcon}
            iconAlt="Euro"
            register={register}
            error={errors.bmev}
            description="Warning! Once the budget is created, the BMEV cannot be reduced until the budget period ends."
          />
          <SliderField
            name="bmevThreshold"
            label="Alerting threshold of BMEV"
            register={register}
            value={[watch('bmevThreshold')]}
            onValueChange={([value]) => setValue('bmevThreshold', value)}
            min={0}
            max={100}
            step={1}
            unit="%"
            error={errors.bmevThreshold}
          />
        </div>
        <div className="flex flex-col justify-between w-full flex-1 space-y-6">
          <InputField
            name="btrt"
            label="Budget total revenue target (BTRT)"
            placeholder="e.g 100000"
            type="number"
            icon={euroIcon}
            iconAlt="Euro"
            register={register}
            error={errors.btrt}
            description="Warning! Once the budget is created, the BTRT cannot be reduced until the budget period ends."
          />
          <SliderField
            name="btrtThreshold"
            label="Alerting threshold of BTRT"
            register={register}
            value={[watch('btrtThreshold')]}
            onValueChange={([value]) => setValue('btrtThreshold', value)}
            min={0}
            max={100}
            step={1}
            unit="%"
            error={errors.btrtThreshold}
          />
        </div>
      </div>
    </>
  );
};

export default BudgetStep;
